import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { chargerService, chargersService } from '../services/api';
import { formatDateForAPIFrom, formatDateForAPITo } from '../utils/dateUtils';

// Chart component for displaying measurement data
const MeasurementChart: React.FC<{
  title: string;
  data: (number | null)[];
  ccs2Data?: (number | null)[];
  timestamps: Date[];
  unit: string;
  color: string;
  dateRange: { start: Date; end: Date };
  loading?: boolean;
  error?: string;
  onZoom?: (start: Date, end: Date) => void;
}> = ({ title, data, ccs2Data, timestamps, unit, color, dateRange, loading, error, onZoom }) => {
  const theme = useTheme();
  
  // Tooltip state
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    ccs1Value: number | null;
    ccs2Value: number | null;
    timestamp: Date | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    ccs1Value: null,
    ccs2Value: null,
    timestamp: null,
  });

  // Vertical line state for mouse tracking
  const [verticalLine, setVerticalLine] = useState<{
    visible: boolean;
    x: number;
  }>({
    visible: false,
    x: 0,
  });

  // Selection state for zoom
  const [selection, setSelection] = useState<{
    isSelecting: boolean;
    startX: number | null;
    endX: number | null;
  }>({
    isSelecting: false,
    startX: null,
    endX: null,
  });
  
  const ccs1Data = data; // Use the passed data for CCS 1
  const ccs2DataToUse = ccs2Data; // Use provided CCS2 data or undefined for single line charts
  
  // Helper function to get time range for calculations
  const getTimeRange = () => {
    const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
    let startMs: number, endMs: number, spanMs: number;
    
    if (isSameDay) {
      startMs = dateRange.start.getTime();
      endMs = dateRange.end.getTime();
      spanMs = Math.max(1, endMs - startMs);
    } else {
      startMs = dateRange.start.getTime();
      const endDate = new Date(dateRange.end);
      endDate.setDate(endDate.getDate() + 1);
      endDate.setHours(0, 0, 0, 0);
      endMs = endDate.getTime();
      spanMs = Math.max(1, endMs - startMs);
    }
    
    return { startMs, endMs, spanMs };
  };
  
  // Helper function to convert x position (percentage) to timestamp
  const xPercentToTimestamp = (xPercent: number): Date => {
    const { startMs, spanMs } = getTimeRange();
    // chartPaddingLeftPct = 0, chartInnerWidthPct = 100, so we can simplify
    const adjustedX = Math.max(0, Math.min(100, xPercent));
    const timestampMs = startMs + (adjustedX / 100) * spanMs;
    return new Date(timestampMs);
  };
  
  // Helper function to format timestamp for tooltip
  const formatTimestamp = (timestamp: Date) => {
    return timestamp.toLocaleString('no-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };
  
  // Helper function to format value for tooltip
  const formatValue = (value: number, unit: string) => {
    if (unit === '%') {
      return `${value.toFixed(1)}%`;
    } else if (unit === 'kW' || unit === 'V' || unit === 'A') {
      return `${value.toFixed(2)} ${unit}`;
    } else if (unit === '°C') {
      return `${value.toFixed(1)}°C`;
    }
    return `${value.toFixed(2)} ${unit}`;
  };
  
  // Mouse event handlers for data point circles (legacy - disabled in favor of mouse move tracking)
  const handleMouseEnter = (event: React.MouseEvent, value: number, timestamp: Date, label: string) => {
    // Disabled - using mouse move tracking instead
  };
  
  const handleMouseLeave = () => {
    // Disabled - using mouse move tracking instead
  };

  // Mouse down handler to start selection
  const handleMouseDown = (event: React.MouseEvent) => {
    // Only start selection on left mouse button
    if (event.button !== 0) return;
    
    // Prevent text selection during drag
    event.preventDefault();
    
    const chartContainer = event.currentTarget.closest('[data-chart-container]') as HTMLElement;
    if (chartContainer) {
      const rect = chartContainer.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const xPercent = (x / rect.width) * 100;
      
      setSelection({
        isSelecting: true,
        startX: xPercent,
        endX: xPercent,
      });
      
      // Hide tooltip and vertical line when selecting
      setVerticalLine(prev => ({ ...prev, visible: false }));
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  };

  // Mouse move handler for vertical line and selection (for all charts)
  const handleMouseMove = (event: React.MouseEvent) => {
    const chartContainer = event.currentTarget.closest('[data-chart-container]') as HTMLElement;
    if (!chartContainer) return;
    
    const rect = chartContainer.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const xPercent = (x / rect.width) * 100;
    
    // If selecting, update selection end
    if (selection.isSelecting && selection.startX !== null) {
      setSelection(prev => ({
        ...prev,
        endX: xPercent,
      }));
      return;
    }
    
    // Otherwise, show tooltip (existing behavior)
    const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
    
    let startMs: number, endMs: number, spanMs: number;
    
    if (isSameDay) {
      // For same day, use the actual start and end times
      startMs = dateRange.start.getTime();
      endMs = dateRange.end.getTime();
      spanMs = Math.max(1, endMs - startMs);
    } else {
      // For different days, use the existing logic (Start date to End date + 1 at 00:00:00)
      startMs = dateRange.start.getTime();
      const endDate = new Date(dateRange.end);
      endDate.setDate(endDate.getDate() + 1);
      endDate.setHours(0, 0, 0, 0); // Set to 00:00:00
      endMs = endDate.getTime();
      spanMs = Math.max(1, endMs - startMs);
    }
    
    // Find the closest data point within a reasonable threshold (2% of chart width)
    const threshold = 2; // 2% of chart width
    let closestDataPoint = null;
    let closestDistance = Infinity;
    
    for (let i = 0; i < ccs1Data.length; i++) {
      const t = timestamps[i]?.getTime();
      if (!t || t < startMs || t > endMs) continue;
      
      const dataPointX = chartPaddingLeftPct + (((t - startMs) / spanMs) * chartInnerWidthPct);
      const distance = Math.abs(xPercent - dataPointX);
      
      if (distance < threshold && distance < closestDistance) {
        closestDistance = distance;
        closestDataPoint = dataPointX;
      }
    }
    
    // Also check CCS2 data if it exists
    if (ccs2DataToUse) {
      for (let i = 0; i < ccs2DataToUse.length; i++) {
        const t = timestamps[i]?.getTime();
        if (!t || t < startMs || t > endMs) continue;
        
        const dataPointX = chartPaddingLeftPct + (((t - startMs) / spanMs) * chartInnerWidthPct);
        const distance = Math.abs(xPercent - dataPointX);
        
        if (distance < threshold && distance < closestDistance) {
          closestDistance = distance;
          closestDataPoint = dataPointX;
        }
      }
    }
    
    if (closestDataPoint !== null) {
      // Find the data point index that corresponds to this x position
      let dataPointIndex = -1;
      for (let i = 0; i < ccs1Data.length; i++) {
        const t = timestamps[i]?.getTime();
        if (!t || t < startMs || t > endMs) continue;
        
        const dataPointX = chartPaddingLeftPct + (((t - startMs) / spanMs) * chartInnerWidthPct);
        if (Math.abs(closestDataPoint - dataPointX) < 0.1) {
          dataPointIndex = i;
          break;
        }
      }
      
      if (dataPointIndex >= 0) {
        const ccs1Value = ccs1Data[dataPointIndex];
        const ccs2Value = ccs2DataToUse ? ccs2DataToUse[dataPointIndex] : null;
        const timestamp = timestamps[dataPointIndex];
        
        setVerticalLine({
          visible: true,
          x: closestDataPoint,
        });
        
        setTooltip({
          visible: true,
          x: event.clientX - rect.left - 120,
          y: event.clientY - rect.top,
          ccs1Value,
          ccs2Value,
          timestamp,
        });
      }
    } else {
      setVerticalLine(prev => ({ ...prev, visible: false }));
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  };

  // Mouse up handler to complete selection and zoom
  const handleMouseUp = (event: React.MouseEvent) => {
    if (!selection.isSelecting || selection.startX === null || selection.endX === null) {
      return;
    }
    
    const startX = Math.min(selection.startX, selection.endX);
    const endX = Math.max(selection.startX, selection.endX);
    
    // Only zoom if selection is meaningful (at least 1% of chart width)
    if (endX - startX > 1 && onZoom) {
      const startTimestamp = xPercentToTimestamp(startX);
      const endTimestamp = xPercentToTimestamp(endX);
      
      // Check if selection spans multiple days
      const startDate = new Date(startTimestamp);
      const endDate = new Date(endTimestamp);
      const isSameDay = startDate.toDateString() === endDate.toDateString();
      
      let newStart: Date, newEnd: Date;
      
      if (isSameDay) {
        // For same day, use the actual selected times
        newStart = new Date(startTimestamp);
        newEnd = new Date(endTimestamp);
      } else {
        // For multiple days, set start to beginning of start day and end to end of end day
        newStart = new Date(startTimestamp);
        newStart.setHours(0, 0, 0, 0);
        
        newEnd = new Date(endTimestamp);
        newEnd.setHours(23, 59, 59, 999);
      }
      
      onZoom(newStart, newEnd);
    }
    
    // Reset selection
    setSelection({
      isSelecting: false,
      startX: null,
      endX: null,
    });
  };

  const handleChartMouseLeave = () => {
    // If selecting, don't reset selection on mouse leave (allow dragging outside)
    if (!selection.isSelecting) {
      setVerticalLine(prev => ({ ...prev, visible: false }));
      setTooltip(prev => ({ ...prev, visible: false }));
    }
  };

  // Global mouse up handler to handle mouse up outside chart
  useEffect(() => {
    const handleGlobalMouseUp = (event: MouseEvent) => {
      if (selection.isSelecting) {
        // Reset selection if mouse up occurs outside
        setSelection({
          isSelecting: false,
          startX: null,
          endX: null,
        });
      }
    };

    if (selection.isSelecting) {
      window.addEventListener('mouseup', handleGlobalMouseUp);
      return () => {
        window.removeEventListener('mouseup', handleGlobalMouseUp);
      };
    }
  }, [selection.isSelecting]);
  
  // Calculate dynamic min/max values based on actual data (ignore nulls)
  const definedCcs1 = ccs1Data.filter((v): v is number => v !== null && !Number.isNaN(v));
  const definedCcs2 = (ccs2DataToUse ?? []).filter((v): v is number => v !== null && !Number.isNaN(v));
  const allDefined = [...definedCcs1, ...definedCcs2];
  const dataMin = allDefined.length ? Math.min(...allDefined) : 0;
  const dataMax = allDefined.length ? Math.max(...allDefined) : 0;
  
  // Special handling for percentage data (0-100%)
  let minValue: number, maxValue: number, range: number;
  
  if (title === 'Kjøretøy SoC' || unit === '%') {
    minValue = 0;
    maxValue = 100;
    range = maxValue - minValue;
  } else {
    // For all other data types, apply new rules:
    // 1. If 0 is in range, ensure 0 is a tick
    // 2. Find YMIN ≤ min and YMAX ≥ max such that (YMAX - YMIN) / 4 is whole
    // 3. All ticks must be whole numbers
    
    const zeroInRange = dataMin <= 0 && dataMax >= 0;
    
    let YMIN: number, YMAX: number;
    
    if (zeroInRange) {
      // Special case: if 0 is the smallest value (dataMin = 0), set YMIN = 0
      if (dataMin === 0) {
        const padding = Math.max(dataMax * 0.1, 1);
        const upperBound = dataMax + padding;
        
        // Calculate step size to accommodate upperBound
        // YMIN = 0, YMAX = 4*step, so step = upperBound / 4
        const roughStep = upperBound / 4;
        let step = Math.ceil(roughStep);
        step = Math.ceil(step / 5) * 5; // Round to nearest multiple of 5
        step = Math.max(step, 5); // Ensure step is at least 5
        
        YMIN = 0;
        YMAX = 4 * step;
      } else {
        // 0 is in range but not the smallest. Find symmetric range around 0
        const absMax = Math.max(Math.abs(dataMin), Math.abs(dataMax));
        const padding = Math.max(absMax * 0.1, 1);
        
        // Calculate step size to make (YMAX - YMIN) / 4 whole
        // We want 0 to be one of the 5 ticks, so make it symmetric: YMIN = -2*step, YMAX = 2*step
        // This gives ticks: [-2*step, -step, 0, step, 2*step]
        // For all ticks to end in 0 or 5, step must be a multiple of 5
        const neededRange = Math.max(
          Math.abs(dataMin) + padding,
          Math.abs(dataMax) + padding
        );
        
        // Calculate step size (need at least neededRange/2, rounded up to nearest multiple of 5)
        let step = Math.ceil(neededRange / 2);
        
        // Round step to nearest multiple of 5 to ensure all ticks end in 0 or 5
        step = Math.ceil(step / 5) * 5;
        
        // Ensure step is at least 5
        step = Math.max(step, 5);
        
        // Set symmetric range around 0 with 0 at the middle tick (index 2 of 5)
        YMIN = -2 * step;
        YMAX = 2 * step;
      }
    } else {
      // 0 is not in range. Find YMIN and YMAX such that (YMAX - YMIN) / 4 is whole
      // All ticks must end in 0 or 5
      const dataRange = dataMax - dataMin;
      const padding = Math.max(dataRange * 0.1, Math.abs(dataMax) * 0.05, 1);
      const totalRange = dataRange + padding * 2;
      
      // Calculate rough step size
      const roughStep = totalRange / 4;
      // Round up to nearest whole number, then round to nearest multiple of 5
      let step = Math.ceil(roughStep);
      step = Math.ceil(step / 5) * 5; // Round to nearest multiple of 5
      step = Math.max(step, 5); // Ensure step is at least 5
      
      // Calculate the total range we need (4 steps)
      let requiredRange = step * 4;
      
      // Center the range around the data
      const lowerBound = dataMin - padding;
      const upperBound = dataMax + padding;
      const center = (lowerBound + upperBound) / 2;
      
      // Calculate target YMIN (center of range)
      const targetYMIN = center - requiredRange / 2;
      
      // Round YMIN down to nearest number ending in 0 or 5
      const targetYMINRounded = Math.floor(targetYMIN);
      const remainder = targetYMINRounded % 10;
      if (remainder <= 5) {
        YMIN = targetYMINRounded - remainder; // Round down to nearest 0 or 5
      } else {
        YMIN = targetYMINRounded - (remainder - 5); // Round down to nearest 5
      }
      
      // YMAX = YMIN + 4*step (ensures (YMAX - YMIN) / 4 = step, which is whole)
      // Since step is multiple of 5 and YMIN ends in 0 or 5, YMAX will also end in 0 or 5
      YMAX = YMIN + requiredRange;
      
      // Verify YMAX is at least upperBound, adjust if needed
      if (YMAX < upperBound) {
        // Calculate new step that will accommodate upperBound
        const minRequiredStep = Math.ceil((upperBound - YMIN) / 4 / 5) * 5;
        step = Math.max(minRequiredStep, step);
        requiredRange = step * 4;
        YMAX = YMIN + requiredRange;
      }
      
      // Ensure YMIN is at most lowerBound (adjust if needed)
      if (YMIN > lowerBound) {
        // Recalculate starting from a lower YMIN
        const targetYMINNew = lowerBound;
        const targetYMINRoundedNew = Math.floor(targetYMINNew);
        const remainderNew = targetYMINRoundedNew % 10;
        let newYMIN: number;
        if (remainderNew <= 5) {
          newYMIN = targetYMINRoundedNew - remainderNew;
        } else {
          newYMIN = targetYMINRoundedNew - (remainderNew - 5);
        }
        
        // Recalculate step to ensure YMAX >= upperBound
        const minStepForRange = Math.ceil((upperBound - newYMIN) / 4 / 5) * 5;
        step = Math.max(minStepForRange, step);
        YMIN = newYMIN;
        YMAX = YMIN + (step * 4);
      }
    }
    
    minValue = YMIN;
    maxValue = YMAX;
    range = maxValue - minValue;
  }
  
  // Generate Y-axis tick values dynamically based on the data range
  const generateYAxisTicks = () => {
    // For percentage data, use fixed ticks
    if (title === 'Kjøretøy SoC' || unit === '%') {
      return [100, 75, 50, 25, 0];
    }
    
    // For other data types, generate 5 ticks with whole number values
    // (YMAX - YMIN) / 4 is guaranteed to be whole, so step is whole
    const step = (maxValue - minValue) / 4;
    const ticks = [];
    for (let i = 4; i >= 0; i--) {
      ticks.push(Math.round(minValue + step * i));
    }
    
    return ticks;
  };
  
  const yAxisTicks = generateYAxisTicks();
  // Add inner padding so lowest grid line sits above x-axis labels
  const chartPaddingBottomPct = 8; // percent of svg height
  // Add top padding for all charts to prevent lines from being cut off at maximum values
  const chartPaddingTopPct = 5;
  const chartInnerHeightPct = 100 - chartPaddingBottomPct - chartPaddingTopPct;
  // Left padding inside SVG is 0 because labels are rendered outside the SVG
  const chartPaddingLeftPct = 0;
  const chartPaddingRightPct = 0;
  const chartInnerWidthPct = 100 - chartPaddingLeftPct - chartPaddingRightPct;

  // Fixed-width rail for Y-axis labels and a gutter to separate labels from the chart
  const yLabelRailWidthPx = 32;
  const yLabelGutterPx = 0;

  if (loading) {
    return (
      <Card sx={{ height: '100%', boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)' }}>
        <CardContent sx={{ p: '5px 16px 16px 16px' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(0,0,0,0.6)',
              fontSize: '14px',
              fontFamily: 'Montserrat',
              fontWeight: 400,
              lineHeight: '1.43',
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Box sx={{ height: 197, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={40} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ height: '100%', boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)' }}>
        <CardContent sx={{ p: '5px 16px 16px 16px' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(0,0,0,0.6)',
              fontSize: '14px',
              fontFamily: 'Montserrat',
              fontWeight: 400,
              lineHeight: '1.43',
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Box sx={{ height: 197, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: '#f44336', fontFamily: 'Montserrat', mb: 1 }}>
              {error}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', fontFamily: 'Montserrat', display: 'block', mb: 2 }}>
              Check the browser console for more details. This might be a CORS issue or network connectivity problem.
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => window.location.reload()}
              sx={{ fontSize: '12px' }}
            >
              Retry
            </Button>
          </Box>
        </CardContent>
      </Card>
    );
  }

  // Handle empty data or no defined points
  if (data.length === 0 || allDefined.length === 0) {
    return (
      <Card sx={{ height: '100%', boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)' }}>
        <CardContent sx={{ p: '5px 16px 16px 16px' }}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(0,0,0,0.6)',
              fontSize: '14px',
              fontFamily: 'Montserrat',
              fontWeight: 400,
              lineHeight: '1.43',
              mb: 1,
            }}
          >
            {title}
          </Typography>
          <Box sx={{ height: 197, position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontFamily: 'Montserrat', mb: 1 }}>
              No data available for the selected date range.
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.6)', fontFamily: 'Montserrat', display: 'block' }}>
              Try selecting a different date range.
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)' }}>
      <CardContent sx={{ p: '5px 16px 16px 16px' }}>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(0,0,0,0.6)',
            fontSize: '14px',
            fontFamily: 'Montserrat',
            fontWeight: 400,
            lineHeight: '1.43',
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Box sx={{ height: 197, position: 'relative' }}>
          {/* Chart area */}
          <Box sx={{ height: 174, p: 2, position: 'relative' }}>
            {/* Fixed-width Y-axis label rail (HTML) */}
            {(() => {
              return (
                <Box sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${yLabelRailWidthPx}px`, pointerEvents: 'none' }}>
                  {yAxisTicks.map((value, index) => {
                    // Compress label positions toward the center tick so outer labels sit closer
                    const relativeIndex = (index / (yAxisTicks.length - 1));
                    const center = 0.5;
                    const labelCompression = 0.8; // 1 = no compression; smaller = closer to center
                    const compressedRelative = center + (relativeIndex - center) * labelCompression;
                    const y = chartPaddingTopPct + (compressedRelative * chartInnerHeightPct);
                    const isEmphasis = index === 0 || index === yAxisTicks.length - 1 || value === 0;
                    return (
                      <Typography
                        key={`ylabel-html-${index}`}
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          top: `${y}%`,
                          transform: 'translateY(-50%)',
                          right: '0px',
                          fontSize: '12px',
                          fontFamily: 'Montserrat',
                          color: isEmphasis ? 'rgba(0,0,0,0.87)' : 'rgba(0,0,0,0.6)',
                          lineHeight: '16px',
                          textAlign: 'right',
                        }}
                      >
                        {String(value)}
                      </Typography>
                    );
                  })}
                </Box>
              );
            })()}
            
            {/* Chart content */}
            <Box 
              data-chart-container 
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleChartMouseLeave}
              sx={{ 
                ml: 3, 
                height: '100%', 
                position: 'relative', 
                pl: `${yLabelRailWidthPx + yLabelGutterPx}px`, 
                cursor: selection.isSelecting ? 'col-resize' : 'default',
                userSelect: 'none',
                WebkitUserSelect: 'none',
              }}
            >
            {/* Chart lines with grid */}
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center' }}>
                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ vectorEffect: 'non-scaling-stroke' }}>
                {/* Grid lines */}
                {yAxisTicks.map((_, index) => {
                  const y = chartPaddingTopPct + (index / (yAxisTicks.length - 1)) * chartInnerHeightPct;
                    return (
                      <line
                        key={index}
                      x1={0}
                        y1={y}
                      x2={100}
                        y2={y}
                        stroke="rgba(0,0,0,0.1)"
                        strokeWidth="1"
                        style={{ vectorEffect: 'non-scaling-stroke'}}
                      />
                    );
                  })}

                {/* Y-axis vertical line */}
                <line
                  x1={0}
                  y1={chartPaddingTopPct}
                  x2={0}
                  y2={chartPaddingTopPct + chartInnerHeightPct}
                  stroke="rgba(0,0,0,0.3)"
                  strokeWidth="1"
                  style={{ vectorEffect: 'non-scaling-stroke' }}
                />

                {/* X-axis tick marks */}
                {(() => {
                  const timeDiff = dateRange.end.getTime() - dateRange.start.getTime();
                  const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
                  
                  // Check if start and end dates are the same day
                  const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
                  
                  // Calculate the actual range
                  let actualStartMs: number, actualEndMs: number, actualSpanMs: number;
                  
                  if (isSameDay) {
                    // For same day, use the actual start and end times
                    actualStartMs = dateRange.start.getTime();
                    actualEndMs = dateRange.end.getTime();
                    actualSpanMs = Math.max(1, actualEndMs - actualStartMs);
                  } else {
                    // For different days, use the existing logic (Start date to End date + 1 at 00:00:00)
                    actualStartMs = dateRange.start.getTime();
                    const endDate = new Date(dateRange.end);
                    endDate.setDate(endDate.getDate() + 1);
                    endDate.setHours(0, 0, 0, 0); // Set to 00:00:00
                    actualEndMs = endDate.getTime();
                    actualSpanMs = actualEndMs - actualStartMs;
                  }
                  
                  // Generate ticks
                  const ticks = [];
                  
                  if (isSameDay) {
                    // Calculate the actual time span in milliseconds
                    const timeSpanMs = actualEndMs - actualStartMs;
                    const timeSpanHours = timeSpanMs / (1000 * 60 * 60);
                    
                    // If time range is below 2 hours, show ticks every 15 minutes
                    if (timeSpanHours < 2) {
                      const tickIntervalMs = 15 * 60 * 1000; // 15 minutes in milliseconds
                      let currentTime = new Date(actualStartMs);
                      let tickIndex = 0;
                      
                      // Round start time down to nearest 15-minute interval
                      const startMinutes = currentTime.getMinutes();
                      const roundedMinutes = Math.floor(startMinutes / 15) * 15;
                      currentTime.setMinutes(roundedMinutes, 0, 0);
                      
                      while (currentTime.getTime() <= actualEndMs) {
                        const tickTime = currentTime.getTime();
                        const x = chartPaddingLeftPct + (((tickTime - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                        
                        // Only show tick if it's within the visible chart range (0-100%)
                        if (x >= 0 && x <= 100) {
                          ticks.push(
                            <line
                              key={`x-tick-${tickIndex}`}
                              x1={x}
                              y1={chartPaddingTopPct + chartInnerHeightPct - 2}
                              x2={x}
                              y2={chartPaddingTopPct + chartInnerHeightPct + 2}
                              stroke="rgba(0,0,0,0.6)"
                              strokeWidth="2"
                              style={{ vectorEffect: 'non-scaling-stroke' }}
                            />
                          );
                        }
                        
                        // Move to next 15-minute interval
                        currentTime = new Date(currentTime.getTime() + tickIntervalMs);
                        tickIndex++;
                      }
                    } else {
                      // For 2 hours or more, show hourly ticks
                      const startHour = dateRange.start.getHours();
                      const endHour = dateRange.end.getHours();
                      const totalHours = endHour - startHour + 1;
                      
                      // Determine tick interval based on total hours
                      let tickIntervalHours: number;
                      if (totalHours <= 6) {
                        tickIntervalHours = 1; // Show every hour
                      } else if (totalHours <= 12) {
                        tickIntervalHours = 2; // Show every 2 hours
                      } else {
                        tickIntervalHours = 3; // Show every 3 hours
                      }
                      
                      let currentHour = startHour;
                      let tickIndex = 0;
                      
                      while (currentHour <= endHour) {
                        const currentTime = new Date(dateRange.start);
                        currentTime.setHours(currentHour, 0, 0, 0);
                        const tickTime = currentTime.getTime();
                        const x = chartPaddingLeftPct + (((tickTime - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                        
                        // Only show tick if it's within the visible chart range (0-100%)
                        if (x >= 0 && x <= 100) {
                          ticks.push(
                            <line
                              key={`x-tick-${tickIndex}`}
                              x1={x}
                              y1={chartPaddingTopPct + chartInnerHeightPct - 2}
                              x2={x}
                              y2={chartPaddingTopPct + chartInnerHeightPct + 2}
                              stroke="rgba(0,0,0,0.6)"
                              strokeWidth="2"
                              style={{ vectorEffect: 'non-scaling-stroke' }}
                            />
                          );
                        }
                        
                        currentHour += tickIntervalHours;
                        tickIndex++;
                      }
                      
                      // Add tick for the end of day (00:00:00 of next day) only if it's within visible range
                      const endTime = new Date(dateRange.end);
                      endTime.setDate(endTime.getDate() + 1);
                      endTime.setHours(0, 0, 0, 0); // Set to 00:00:00 of next day
                      const endTimeMs = endTime.getTime();
                      const endX = chartPaddingLeftPct + (((endTimeMs - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                      
                      // Only show end tick if it's within the visible chart range
                      if (endX >= 0 && endX <= 100) {
                        ticks.push(
                          <line
                            key={`x-tick-end`}
                            x1={endX}
                            y1={chartPaddingTopPct + chartInnerHeightPct - 2}
                            x2={endX}
                            y2={chartPaddingTopPct + chartInnerHeightPct + 2}
                            stroke="rgba(0,0,0,0.6)"
                            strokeWidth="2"
                            style={{ vectorEffect: 'non-scaling-stroke' }}
                          />
                        );
                      }
                    }
                  } else {
                    // For different days, use the existing logic
                    let tickIntervalDays: number;
                    if (daysDiff <= 1) {
                      tickIntervalDays = 1; // Show every day
                    } else if (daysDiff <= 7) {
                      tickIntervalDays = 1; // Show every day for up to a week
                    } else if (daysDiff <= 14) {
                      tickIntervalDays = 2; // Show every 2 days
                    } else if (daysDiff <= 30) {
                      tickIntervalDays = 3; // Show every 3 days
                    } else if (daysDiff <= 60) {
                      tickIntervalDays = 5; // Show every 5 days
                    } else if (daysDiff <= 90) {
                      tickIntervalDays = 7; // Show every week
                    } else {
                      tickIntervalDays = 14; // Show every 2 weeks
                    }
                    
                    const startDate = new Date(dateRange.start);
                    let currentDate = new Date(startDate);
                    let tickIndex = 0;
                    
                    while (currentDate.getTime() <= actualEndMs) {
                      const tickTime = currentDate.getTime();
                      const x = chartPaddingLeftPct + (((tickTime - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                      
                      // Only show tick if it's within the visible chart range (0-100%)
                      if (x >= 0 && x <= 100) {
                        ticks.push(
                          <line
                            key={`x-tick-${tickIndex}`}
                            x1={x}
                            y1={chartPaddingTopPct + chartInnerHeightPct - 2}
                            x2={x}
                            y2={chartPaddingTopPct + chartInnerHeightPct + 2}
                            stroke="rgba(0,0,0,0.6)"
                            strokeWidth="2"
                            style={{ vectorEffect: 'non-scaling-stroke' }}
                          />
                        );
                      }
                      
                      // Move to next tick
                      currentDate.setDate(currentDate.getDate() + tickIntervalDays);
                      tickIndex++;
                    }
                  }
                  
                  return ticks;
                })()}
                
                {/* Selection rectangle for zoom */}
                {selection.isSelecting && selection.startX !== null && selection.endX !== null && (
                  <rect
                    x={Math.min(selection.startX, selection.endX)}
                    y={chartPaddingTopPct}
                    width={Math.abs(selection.endX - selection.startX)}
                    height={chartInnerHeightPct}
                    fill="rgba(25, 118, 210, 0.1)"
                    stroke="rgba(25, 118, 210, 0.5)"
                    strokeWidth="1"
                    style={{ pointerEvents: 'none' }}
                  />
                )}

                {/* Vertical line for mouse tracking (for all charts) */}
                {verticalLine.visible && !selection.isSelecting && (
                  <line
                    x1={verticalLine.x}
                    y1={chartPaddingTopPct}
                    x2={verticalLine.x}
                    y2={chartPaddingTopPct + chartInnerHeightPct}
                    stroke="#B5AFA9"
                    strokeWidth="1"
                   // strokeDasharray="2,2"
                    style={{ vectorEffect: 'non-scaling-stroke' }}
                  />
                )}
                  
                {/* CCS 1 Line - render continuous segments only (skip nulls) */}
                {(() => {
                  const segments: string[] = [];
                  let current: string[] = [];
                  
                  // Check if start and end dates are the same day
                  const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
                  
                  let startMs: number, endMs: number, spanMs: number;
                  
                  if (isSameDay) {
                    // For same day, use the actual start and end times
                    startMs = dateRange.start.getTime();
                    endMs = dateRange.end.getTime();
                    spanMs = Math.max(1, endMs - startMs);
                  } else {
                    // For different days, use the existing logic (Start date to End date + 1 at 00:00:00)
                    startMs = dateRange.start.getTime();
                    const endDate = new Date(dateRange.end);
                    endDate.setDate(endDate.getDate() + 1);
                    endDate.setHours(0, 0, 0, 0); // Set to 00:00:00
                    endMs = endDate.getTime();
                    spanMs = Math.max(1, endMs - startMs);
                  }
                  for (let i = 0; i < ccs1Data.length; i++) {
                    const value = ccs1Data[i];
                    const t = timestamps[i]?.getTime();
                    if (!t || t < startMs || t > endMs) {
                      if (current.length > 0) {
                        segments.push(current.join(' '));
                        current = [];
                      }
                      continue;
                    }
                    const x = chartPaddingLeftPct + (((t - startMs) / spanMs) * chartInnerWidthPct);
                    if (typeof value === 'number') {
                      const y = chartPaddingTopPct + (1 - ((value - minValue) / range)) * chartInnerHeightPct;
                      if (current.length === 0) {
                        current.push(`M ${x},${y}`);
                      } else {
                        current.push(`L ${x},${y}`);
                      }
                    } else if (current.length > 0) {
                      segments.push(current.join(' '));
                      current = [];
                    }
                  }
                  if (current.length > 0) segments.push(current.join(' '));
                  return segments.map((d, idx) => (
                    <path
                      key={`ccs1-${idx}`}
                      d={d}
                      fill="none"
                      stroke={theme.palette.secondary.main}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ vectorEffect: 'non-scaling-stroke' }}
                    />
                  ));
                })()}
                
                  {/* CCS 2 Line - only show if ccs2DataToUse is provided */}
                {ccs2DataToUse && (() => {
                  const segments: string[] = [];
                  let current: string[] = [];
                  
                  // Check if start and end dates are the same day
                  const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
                  
                  let startMs: number, endMs: number, spanMs: number;
                  
                  if (isSameDay) {
                    // For same day, use the actual start and end times
                    startMs = dateRange.start.getTime();
                    endMs = dateRange.end.getTime();
                    spanMs = Math.max(1, endMs - startMs);
                  } else {
                    // For different days, use the existing logic (Start date to End date + 1 at 00:00:00)
                    startMs = dateRange.start.getTime();
                    const endDate = new Date(dateRange.end);
                    endDate.setDate(endDate.getDate() + 1);
                    endDate.setHours(0, 0, 0, 0); // Set to 00:00:00
                    endMs = endDate.getTime();
                    spanMs = Math.max(1, endMs - startMs);
                  }
                  for (let i = 0; i < ccs2DataToUse.length; i++) {
                    const value = ccs2DataToUse[i];
                    const t = timestamps[i]?.getTime();
                    if (!t || t < startMs || t > endMs) {
                      if (current.length > 0) {
                        segments.push(current.join(' '));
                        current = [];
                      }
                      continue;
                    }
                    const x = chartPaddingLeftPct + (((t - startMs) / spanMs) * chartInnerWidthPct);
                    if (typeof value === 'number') {
                      const y = chartPaddingTopPct + (1 - ((value - minValue) / range)) * chartInnerHeightPct;
                      if (current.length === 0) {
                        current.push(`M ${x},${y}`);
                      } else {
                        current.push(`L ${x},${y}`);
                      }
                    } else if (current.length > 0) {
                      segments.push(current.join(' '));
                      current = [];
                    }
                  }
                  if (current.length > 0) segments.push(current.join(' '));
                  return segments.map((d, idx) => (
                    <path
                      key={`ccs2-${idx}`}
                      d={d}
                      fill="none"
                      stroke={theme.palette.primary.main}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{ vectorEffect: 'non-scaling-stroke' }}
                    />
                  ));
                })()}
                
                </svg>
              </Box>
              
              {/* Data Points Overlay */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: 'none',
                }}
              >
                {/* CCS 1 Data Points */}
                {(() => {
                  // Check if start and end dates are the same day
                  const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
                  
                  let startMs: number, endMs: number, spanMs: number;
                  
                  if (isSameDay) {
                    // For same day, use the actual start and end times
                    startMs = dateRange.start.getTime();
                    endMs = dateRange.end.getTime();
                    spanMs = Math.max(1, endMs - startMs);
                  } else {
                    // For different days, use the existing logic (Start date to End date + 1 at 00:00:00)
                    startMs = dateRange.start.getTime();
                    const endDate = new Date(dateRange.end);
                    endDate.setDate(endDate.getDate() + 1);
                    endDate.setHours(0, 0, 0, 0); // Set to 00:00:00
                    endMs = endDate.getTime();
                    spanMs = Math.max(1, endMs - startMs);
                  }
                  return ccs1Data.map((value, i) => {
                    const t = timestamps[i]?.getTime();
                    if (!t || t < startMs || t > endMs || typeof value !== 'number') {
                      return null;
                    }
                    const x = chartPaddingLeftPct + (((t - startMs) / spanMs) * chartInnerWidthPct);
                    const y = chartPaddingTopPct + (1 - ((value - minValue) / range)) * chartInnerHeightPct;
                    const label = ccs2DataToUse ? 'CCS 1' : title;
                    
                    return (
                      <Box
                        key={`ccs1-point-${i}`}
                        sx={{
                          position: 'absolute',
                          left: `${x}%`,
                          top: `${y}%`,
                          width: '1px',
                          height: '1px',
                          borderRadius: '50%',
                          transform: 'translate(-50%, -50%)',
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                          zIndex: 10,
                        }}
                        onMouseEnter={(e) => handleMouseEnter(e, value, timestamps[i], label)}
                        onMouseLeave={handleMouseLeave}
                      />
                    );
                  }).filter(Boolean);
                })()}
                
                {/* CCS 2 Data Points */}
                {ccs2DataToUse && (() => {
                  // Check if start and end dates are the same day
                  const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
                  
                  let startMs: number, endMs: number, spanMs: number;
                  
                  if (isSameDay) {
                    // For same day, use the actual start and end times
                    startMs = dateRange.start.getTime();
                    endMs = dateRange.end.getTime();
                    spanMs = Math.max(1, endMs - startMs);
                  } else {
                    // For different days, use the existing logic (Start date to End date + 1 at 00:00:00)
                    startMs = dateRange.start.getTime();
                    const endDate = new Date(dateRange.end);
                    endDate.setDate(endDate.getDate() + 1);
                    endDate.setHours(0, 0, 0, 0); // Set to 00:00:00
                    endMs = endDate.getTime();
                    spanMs = Math.max(1, endMs - startMs);
                  }
                  return ccs2DataToUse.map((value, i) => {
                    const t = timestamps[i]?.getTime();
                    if (!t || t < startMs || t > endMs || typeof value !== 'number') {
                      return null;
                    }
                    const x = chartPaddingLeftPct + (((t - startMs) / spanMs) * chartInnerWidthPct);
                    const y = chartPaddingTopPct + (1 - ((value - minValue) / range)) * chartInnerHeightPct;
                    
                    return (
                      <Box
                        key={`ccs2-point-${i}`}
                        sx={{
                          position: 'absolute',
                          left: `${x}%`,
                          top: `${y}%`,
                          width: '1px',
                          height: '1px',
                          borderRadius: '50%',
                          backgroundColor: theme.palette.primary.main,
                          transform: 'translate(-50%, -50%)',
                          cursor: 'pointer',
                          pointerEvents: 'auto',
                          zIndex: 10,
                        }}
                        onMouseEnter={(e) => handleMouseEnter(e, value, timestamps[i], 'CCS 2')}
                        onMouseLeave={handleMouseLeave}
                      />
                    );
                  }).filter(Boolean);
                })()}
              </Box>
              
              {/* Tooltip */}
              {tooltip.visible && tooltip.timestamp && !selection.isSelecting && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: tooltip.x + 10,
                    top: tooltip.y + 10,
                    backgroundColor: '#413C38',
                    color: 'white',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontFamily: 'Montserrat',
                    zIndex: 1000,
                    pointerEvents: 'none',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                    maxWidth: '240px',
                  }}
                >
                  <Box sx={{ fontWeight: 600, mb: 0.5, fontSize: '11px', opacity: 0.9 }}>
                    {formatTimestamp(tooltip.timestamp)}
                  </Box>
                  {tooltip.ccs1Value !== null && (
                    <Box sx={{ mb: 0.5 }}>
                      {ccs2DataToUse ? 'CCS1' : title}: {formatValue(tooltip.ccs1Value, unit)}
                    </Box>
                  )}
                  {tooltip.ccs2Value !== null && (
                    <Box sx={{ mb: 0.5 }}>
                      CCS2: {formatValue(tooltip.ccs2Value, unit)}
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          
          {/* X-axis labels positioned below ticks */}
          <Box sx={{ position: 'relative', ml: 5, pl: `${yLabelRailWidthPx + yLabelGutterPx}px`, mr: 2, pb: 1, height: '20px' }}>
            {(() => {
              const timeDiff = dateRange.end.getTime() - dateRange.start.getTime();
              const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
              
              // Check if start and end dates are the same day
              const isSameDay = dateRange.start.toDateString() === dateRange.end.toDateString();
              
              // Calculate the actual range
              let actualStartMs: number, actualEndMs: number, actualSpanMs: number;
              
              if (isSameDay) {
                // For same day, use the actual start and end times
                actualStartMs = dateRange.start.getTime();
                actualEndMs = dateRange.end.getTime();
                actualSpanMs = Math.max(1, actualEndMs - actualStartMs);
              } else {
                // For different days, use the existing logic (Start date to End date + 1 at 00:00:00)
                actualStartMs = dateRange.start.getTime();
                const endDate = new Date(dateRange.end);
                endDate.setDate(endDate.getDate() + 1);
                endDate.setHours(0, 0, 0, 0); // Set to 00:00:00
                actualEndMs = endDate.getTime();
                actualSpanMs = actualEndMs - actualStartMs;
              }
              
              // Generate labels
              const labels = [];
              
              if (isSameDay) {
                // Calculate the actual time span in milliseconds
                const timeSpanMs = actualEndMs - actualStartMs;
                const timeSpanHours = timeSpanMs / (1000 * 60 * 60);
                
                // Format time for labels (HH:MM)
                const formatTime = (date: Date) => {
                  const hours = date.getHours().toString().padStart(2, '0');
                  const minutes = date.getMinutes().toString().padStart(2, '0');
                  return `${hours}:${minutes}`;
                };
                
                // If time range is below 2 hours, show labels every 15 minutes
                if (timeSpanHours < 2) {
                  const tickIntervalMs = 15 * 60 * 1000; // 15 minutes in milliseconds
                  let currentTime = new Date(actualStartMs);
                  let labelIndex = 0;
                  
                  // Round start time down to nearest 15-minute interval
                  const startMinutes = currentTime.getMinutes();
                  const roundedMinutes = Math.floor(startMinutes / 15) * 15;
                  currentTime.setMinutes(roundedMinutes, 0, 0);
                  
                  while (currentTime.getTime() <= actualEndMs) {
                    const tickTime = currentTime.getTime();
                    const x = chartPaddingLeftPct + (((tickTime - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                    
                    // Only show label if it's within the visible chart range (0-100%)
                    if (x >= 0 && x <= 100) {
                      labels.push(
                        <Typography
                          key={`x-label-${labelIndex}`}
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            left: `${x}%`,
                            transform: 'translateX(-50%)',
                            fontSize: '12px',
                            fontFamily: 'Montserrat',
                            color: 'rgba(0,0,0,0.6)',
                            lineHeight: '12px',
                            letterSpacing: '0.15px',
                          }}
                        >
                          {formatTime(currentTime)}
                        </Typography>
                      );
                    }
                    
                    // Move to next 15-minute interval
                    currentTime = new Date(currentTime.getTime() + tickIntervalMs);
                    labelIndex++;
                  }
                } else {
                  // For 2 hours or more, show hourly labels
                  const startHour = dateRange.start.getHours();
                  const endHour = dateRange.end.getHours();
                  const totalHours = endHour - startHour + 1;
                  
                  // Determine tick interval based on total hours (same as tick generation)
                  let tickIntervalHours: number;
                  if (totalHours <= 6) {
                    tickIntervalHours = 1; // Show every hour
                  } else if (totalHours <= 12) {
                    tickIntervalHours = 2; // Show every 2 hours
                  } else {
                    tickIntervalHours = 3; // Show every 3 hours
                  }
                  
                  let currentHour = startHour;
                  let labelIndex = 0;
                  
                  while (currentHour <= endHour) {
                    const currentTime = new Date(dateRange.start);
                    currentTime.setHours(currentHour, 0, 0, 0);
                    const tickTime = currentTime.getTime();
                    const x = chartPaddingLeftPct + (((tickTime - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                    
                    // Only show label if it's within the visible chart range (0-100%)
                    if (x >= 0 && x <= 100) {
                      labels.push(
                        <Typography
                          key={`x-label-${labelIndex}`}
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            left: `${x}%`,
                            transform: 'translateX(-50%)',
                            fontSize: '12px',
                            fontFamily: 'Montserrat',
                            color: 'rgba(0,0,0,0.6)',
                            lineHeight: '12px',
                            letterSpacing: '0.15px',
                          }}
                        >
                          {formatTime(currentTime)}
                        </Typography>
                      );
                    }
                    
                    currentHour += tickIntervalHours;
                    labelIndex++;
                  }
                  
                  // Add label for the end of day (00:00:00 of next day) only if it's within visible range
                  const endTime = new Date(dateRange.end);
                  endTime.setDate(endTime.getDate() + 1);
                  endTime.setHours(0, 0, 0, 0); // Set to 00:00:00 of next day
                  const endTimeMs = endTime.getTime();
                  const endX = chartPaddingLeftPct + (((endTimeMs - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                  
                  // Only show end label if it's within the visible chart range
                  if (endX >= 0 && endX <= 100) {
                    labels.push(
                      <Typography
                        key={`x-label-end`}
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          left: `${endX}%`,
                          transform: 'translateX(-50%)',
                          fontSize: '12px',
                          fontFamily: 'Montserrat',
                          color: 'rgba(0,0,0,0.6)',
                          lineHeight: '12px',
                          letterSpacing: '0.15px',
                        }}
                      >
                        {formatTime(endTime)}
                      </Typography>
                    );
                  }
                }
              } else {
                // For different days, use the existing logic
                let tickIntervalDays: number;
                if (daysDiff <= 1) {
                  tickIntervalDays = 1; // Show every day
                } else if (daysDiff <= 7) {
                  tickIntervalDays = 1; // Show every day for up to a week
                } else if (daysDiff <= 14) {
                  tickIntervalDays = 2; // Show every 2 days
                } else if (daysDiff <= 30) {
                  tickIntervalDays = 3; // Show every 3 days
                } else if (daysDiff <= 60) {
                  tickIntervalDays = 5; // Show every 5 days
                } else if (daysDiff <= 90) {
                  tickIntervalDays = 7; // Show every week
                } else {
                  tickIntervalDays = 14; // Show every 2 weeks
                }
                
                // Format date for labels
                const formatDate = (date: Date) => {
                  const day = date.getDate();
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const month = monthNames[date.getMonth()];
                  return `${day} ${month}`;
                };
                
                const startDate = new Date(dateRange.start);
                let currentDate = new Date(startDate);
                let labelIndex = 0;
                
                while (currentDate.getTime() <= actualEndMs) {
                  const tickTime = currentDate.getTime();
                  const x = chartPaddingLeftPct + (((tickTime - actualStartMs) / actualSpanMs) * chartInnerWidthPct);
                  
                  // Only show label if it's within the visible chart range (0-100%)
                  if (x >= 0 && x <= 100) {
                    labels.push(
                      <Typography
                        key={`x-label-${labelIndex}`}
                        variant="caption"
                        sx={{
                          position: 'absolute',
                          left: `${x}%`,
                          transform: 'translateX(-50%)',
                          fontSize: '12px',
                          fontFamily: 'Montserrat',
                          color: 'rgba(0,0,0,0.6)',
                          lineHeight: '12px',
                          letterSpacing: '0.15px',
                        }}
                      >
                        {formatDate(currentDate)}
                      </Typography>
                    );
                  }
                  
                  // Move to next label
                  currentDate.setDate(currentDate.getDate() + tickIntervalDays);
                  labelIndex++;
                }
              }
              
              return labels;
            })()}
          </Box>
        </Box>
        
        {/* Legend */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 16, height: 8, backgroundColor: theme.palette.secondary.main, borderRadius: 1 }} />
            <Typography
              variant="caption"
              sx={{
                fontSize: '10px',
                fontFamily: 'Montserrat',
                fontWeight: 500,
                color: 'rgba(0,0,0,0.87)',
                lineHeight: '14px',
              }}
            >
              {ccs2DataToUse ? 'CCS 1' : title}
            </Typography>
          </Box>
          {ccs2DataToUse && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 8, backgroundColor: theme.palette.primary.main, borderRadius: 1 }} />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '10px',
                  fontFamily: 'Montserrat',
                  fontWeight: 500,
                  color: 'rgba(0,0,0,0.87)',
                  lineHeight: '14px',
                }}
              >
                CCS 2
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

const ChargerDashboard: React.FC = () => {
  const theme = useTheme();
  const { unitId, orgId } = useParams<{ unitId: string; orgId: string }>();
  const [showMoreCharts, setShowMoreCharts] = useState(false);
  const [chargerName, setChargerName] = useState<string | null>(null);
  const [chargerModel, setChargerModel] = useState<string | null>(null);
  const [chargerNameLoading, setChargerNameLoading] = useState(true);
  
  // Date range state - default to today 00:00 to today 23:59:59
  const [dateRange, setDateRange] = useState(() => {
    const startDate = new Date(); // Today
    startDate.setHours(0, 0, 0, 0); // Set to start of day (00:00:00)
    
    const endDate = new Date(); // Today
    endDate.setHours(23, 59, 59, 999); // Set to end of day (23:59:59)
    
    return { start: startDate, end: endDate };
  });

  // Store the original date range (from date pickers, not from zoom)
  const [originalDateRange, setOriginalDateRange] = useState(() => {
    const startDate = new Date(); // Today
    startDate.setHours(0, 0, 0, 0); // Set to start of day (00:00:00)
    
    const endDate = new Date(); // Today
    endDate.setHours(23, 59, 59, 999); // Set to end of day (23:59:59)
    
    return { start: startDate, end: endDate };
  });

  // Check if currently zoomed (date range differs from original)
  const isZoomed = useMemo(() => {
    return (
      dateRange.start.getTime() !== originalDateRange.start.getTime() ||
      dateRange.end.getTime() !== originalDateRange.end.getTime()
    );
  }, [dateRange, originalDateRange]);

  // Reset zoom function
  const handleResetZoom = () => {
    setDateRange({
      start: new Date(originalDateRange.start),
      end: new Date(originalDateRange.end),
    });
  };

  // Refs to prevent duplicate API calls in StrictMode
  const chargerNameFetchedRef = useRef<string | null>(null);
  const mainChartsFetchedRef = useRef<string | null>(null);
  const additionalChartsFetchedRef = useRef<string | null>(null);

  // Check if charger name starts with "KE"
  const shouldConvertToKW = useMemo(() => {
    return chargerName?.startsWith('KE') ?? false;
  }, [chargerName]);

  // Check if model is Mjølner or Boostpoint (these units don't have battery charts)
  const shouldHideBatteryCharts = useMemo(() => {
    return chargerModel === 'Mjølner' || chargerModel === 'Boostpoint';
  }, [chargerModel]);

  // Fetch charger details to get charger name
  useEffect(() => {
    const dependencyKey = `${unitId}-${orgId}`;
    
    // Prevent duplicate calls in StrictMode
    if (chargerNameFetchedRef.current === dependencyKey) return;
    chargerNameFetchedRef.current = dependencyKey;

    const fetchChargerName = async () => {
      if (!unitId || !orgId) {
        setChargerNameLoading(false);
        return;
      }

      try {
        setChargerNameLoading(true);
        const response = await chargersService.getCharger(orgId, unitId);
        if (response.success && response.data && response.data.units && response.data.units.length > 0) {
          // Find the charger with the matching unit_id
          const charger = response.data.units.find((unit: any) => unit.unit_id === unitId);
          if (charger) {
            setChargerName(charger.unit_name);
            setChargerModel(charger.model);
          } else {
            // Charger not found in the response
            setChargerName(null);
            setChargerModel(null);
          }
        } else {
          // If we can't fetch charger name, assume it's not a KE charger
          setChargerName(null);
          setChargerModel(null);
        }
      } catch (error) {
        console.error('Error fetching charger name:', error);
        // On error, assume it's not a KE charger
        setChargerName(null);
        setChargerModel(null);
      } finally {
        setChargerNameLoading(false);
      }
    };

    fetchChargerName();
  }, [unitId, orgId]);

  // CCS Voltage data state
  const [ccsVoltageData, setCcsVoltageData] = useState<{
    ccs1Data: (number | null)[];
    ccs2Data: (number | null)[];
    timestamps: Date[];
  }>({ ccs1Data: [], ccs2Data: [], timestamps: [] });
  const [ccsVoltageLoading, setCcsVoltageLoading] = useState(false);
  const [ccsVoltageError, setCcsVoltageError] = useState<string | null>(null);

  // CCS Power data state
  const [ccsPowerData, setCcsPowerData] = useState<{
    ccs1Data: (number | null)[];
    ccs2Data: (number | null)[];
    timestamps: Date[];
  }>({ ccs1Data: [], ccs2Data: [], timestamps: [] });
  const [ccsPowerLoading, setCcsPowerLoading] = useState(false);
  const [ccsPowerError, setCcsPowerError] = useState<string | null>(null);

  // CCS EV SoC data state
  const [ccsEVSOCData, setCcsEVSOCData] = useState<{
    ccs1Data: (number | null)[];
    ccs2Data: (number | null)[];
    timestamps: Date[];
  }>({ ccs1Data: [], ccs2Data: [], timestamps: [] });
  const [ccsEVSOCLoading, setCcsEVSOCLoading] = useState(false);
  const [ccsEVSOCError, setCcsEVSOCError] = useState<string | null>(null);

  // CCS Current data state
  const [ccsCurrentData, setCcsCurrentData] = useState<{
    ccs1Data: (number | null)[];
    ccs2Data: (number | null)[];
    timestamps: Date[];
  }>({ ccs1Data: [], ccs2Data: [], timestamps: [] });
  const [ccsCurrentLoading, setCcsCurrentLoading] = useState(false);
  const [ccsCurrentError, setCcsCurrentError] = useState<string | null>(null);

  // Battery SOC data state
  const [batterySOCData, setBatterySOCData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [batterySOCLoading, setBatterySOCLoading] = useState(false);
  const [batterySOCError, setBatterySOCError] = useState<string | null>(null);

  // Inlet Power data state
  const [inletPowerData, setInletPowerData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [inletPowerLoading, setInletPowerLoading] = useState(false);
  const [inletPowerError, setInletPowerError] = useState<string | null>(null);

  // Battery Temperature data state
  const [batteryTempData, setBatteryTempData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [batteryTempLoading, setBatteryTempLoading] = useState(false);
  const [batteryTempError, setBatteryTempError] = useState<string | null>(null);

  // Battery Voltage data state
  const [batteryVoltageData, setBatteryVoltageData] = useState<{ values: (number | null)[]; timestamps: Date[] }>({ values: [], timestamps: [] });
  const [batteryVoltageLoading, setBatteryVoltageLoading] = useState(false);
  const [batteryVoltageError, setBatteryVoltageError] = useState<string | null>(null);

  // Use dynamic values from route params
  const ORG_ID = orgId || 'a2d44591-7bc0-47f4-a432-528ea088e02b';
  const UNIT_ID = unitId || '520e4ddd-50b9-4a20-98fb-2443a087305d';

  // Determine if we should downsample (only when range > 3 days)
  const shouldDownsampleTo15Min = useMemo(() => {
    const rangeMs = dateRange.end.getTime() - dateRange.start.getTime();
    return rangeMs > 3 * 24 * 60 * 60 * 1000;
  }, [dateRange]);

  // Downsample helper: keeps the first point seen in each 15-minute bucket, preserving alignment across series
  const downsampleTo15Min = <T,>(timestamps: Date[], ...series: Array<T[]>): { timestamps: Date[]; series: Array<T[]> } => {
    if (timestamps.length === 0) {
      return { timestamps: [], series: series.map(() => []) };
    }
    const bucketSizeMs = 15 * 60 * 1000;
    const seenBuckets = new Set<number>();
    const sampledTimestamps: Date[] = [];
    const sampledSeries: Array<T[]> = series.map(() => []);

    for (let i = 0; i < timestamps.length; i++) {
      const t = timestamps[i];
      const bucketKey = Math.floor(t.getTime() / bucketSizeMs);
      if (seenBuckets.has(bucketKey)) continue;
      seenBuckets.add(bucketKey);
      sampledTimestamps.push(t);
      for (let s = 0; s < series.length; s++) {
        sampledSeries[s].push(series[s][i]);
      }
    }

    return { timestamps: sampledTimestamps, series: sampledSeries };
  };

  // Helper function to merge CCS data points by timestamp
  const mergeCCSDataByTimestamp = (
    data: any[],
    ccs1Field: string,
    ccs2Field: string
  ): { ccs1Data: (number | null)[]; ccs2Data: (number | null)[]; timestamps: Date[] } => {
    // Group data points by timestamp
    const timestampMap = new Map<string, { ccs1: number | null; ccs2: number | null; timestamp: Date }>();
    
    for (const point of data) {
      const timestamp = new Date(point.timestamp);
      const timestampKey = timestamp.getTime().toString();
      
      const existing = timestampMap.get(timestampKey);
      if (existing) {
        // Merge: use non-null values from either point
        // If existing value is null, use the new value (if not null)
        // If existing value is not null, keep it (don't overwrite with null)
        if (existing.ccs1 == null && point[ccs1Field] != null) {
          existing.ccs1 = point[ccs1Field];
        }
        if (existing.ccs2 == null && point[ccs2Field] != null) {
          existing.ccs2 = point[ccs2Field];
        }
      } else {
        // First point with this timestamp
        timestampMap.set(timestampKey, {
          ccs1: point[ccs1Field] != null ? point[ccs1Field] : null,
          ccs2: point[ccs2Field] != null ? point[ccs2Field] : null,
          timestamp,
        });
      }
    }
    
    // Convert map to sorted arrays
    const sortedEntries = Array.from(timestampMap.values()).sort(
      (a, b) => a.timestamp.getTime() - b.timestamp.getTime()
    );
    
    return {
      ccs1Data: sortedEntries.map(entry => entry.ccs1),
      ccs2Data: sortedEntries.map(entry => entry.ccs2),
      timestamps: sortedEntries.map(entry => entry.timestamp),
    };
  };

  // Fetch CCS voltage and power data when date range changes
  useEffect(() => {
    const dependencyKey = `${unitId}-${orgId}-${dateRange.start.getTime()}-${dateRange.end.getTime()}`;
    
    // Prevent duplicate calls in StrictMode
    if (mainChartsFetchedRef.current === dependencyKey) return;
    mainChartsFetchedRef.current = dependencyKey;

    const fetchCCSPowerData = async () => {
      setCcsPowerLoading(true);
      setCcsPowerError(null);

      try {
        const fromDate = formatDateForAPIFrom(dateRange.start);
        const toDate = formatDateForAPITo(dateRange.end);
        
        const response = await chargerService.getCCSPower(ORG_ID, UNIT_ID, fromDate, toDate);
        
        if (response.success && response.data) {
          const merged = mergeCCSDataByTimestamp(response.data.data, 'ccs1_power', 'ccs2_power');
          setCcsPowerData(merged);
        } else {
          setCcsPowerError(response.message || 'Failed to fetch CCS power data');
        }
      } catch (error) {
        console.error('Error fetching CCS power data:', error);
        setCcsPowerError(error instanceof Error ? error.message : 'Unknown error occurred');
      } finally {
        setCcsPowerLoading(false);
      }
    };

    const fetchCCSEVSOCData = async () => {
      setCcsEVSOCLoading(true);
      setCcsEVSOCError(null);

      try {
        const fromDate = formatDateForAPIFrom(dateRange.start);
        const toDate = formatDateForAPITo(dateRange.end);
        
        const response = await chargerService.getCCSEVSOC(ORG_ID, UNIT_ID, fromDate, toDate);
        
        if (response.success && response.data) {
          const merged = mergeCCSDataByTimestamp(response.data.data, 'ccs1_ev_soc', 'ccs2_ev_soc');
          setCcsEVSOCData(merged);
        } else {
          setCcsEVSOCError(response.message || 'Failed to fetch CCS EV SoC data');
        }
      } catch (error) {
        console.error('Error fetching CCS EV SoC data:', error);
        setCcsEVSOCError(error instanceof Error ? error.message : 'Unknown error occurred');
      } finally {
        setCcsEVSOCLoading(false);
      }
    };

    const fetchBatterySOCData = async () => {
      // Skip fetching if model is Mjølner or Boostpoint
      if (chargerModel === 'Mjølner' || chargerModel === 'Boostpoint') {
        setBatterySOCData({ values: [], timestamps: [] });
        setBatterySOCLoading(false);
        return;
      }

      setBatterySOCLoading(true);
      setBatterySOCError(null);

      try {
        const fromDate = formatDateForAPIFrom(dateRange.start);
        const toDate = formatDateForAPITo(dateRange.end);
        
        const response = await chargerService.getBatterySOC(ORG_ID, UNIT_ID, fromDate, toDate);
        
        if (response.success && response.data) {
          const values = response.data.data.map((point: any) => point.battery_soc ?? null);
          const timestamps = response.data.data.map((point: any) => new Date(point.timestamp));
          
          setBatterySOCData({ values, timestamps });
        } else {
          setBatterySOCError(response.message || 'Failed to fetch Battery SOC data');
        }
      } catch (error) {
        console.error('Error fetching Battery SOC data:', error);
        setBatterySOCError(error instanceof Error ? error.message : 'Unknown error occurred');
      } finally {
        setBatterySOCLoading(false);
      }
    };

    const fetchInletPowerData = async () => {
      setInletPowerLoading(true);
      setInletPowerError(null);

      try {
        const fromDate = formatDateForAPIFrom(dateRange.start);
        const toDate = formatDateForAPITo(dateRange.end);
        
        const response = await chargerService.getInletPower(ORG_ID, UNIT_ID, fromDate, toDate);
        
        if (response.success && response.data) {
          const values = response.data.data.map((point: any) => point.inlet_power ?? null);
          const timestamps = response.data.data.map((point: any) => new Date(point.timestamp));
          
          setInletPowerData({ values, timestamps });
        } else {
          setInletPowerError(response.message || 'Failed to fetch Inlet Power data');
        }
      } catch (error) {
        console.error('Error fetching Inlet Power data:', error);
        setInletPowerError(error instanceof Error ? error.message : 'Unknown error occurred');
      } finally {
        setInletPowerLoading(false);
      }
    };

    // Always fetch these 4 charts immediately
    fetchCCSPowerData();
    fetchCCSEVSOCData();
    fetchBatterySOCData();
    fetchInletPowerData();
  }, [dateRange, unitId, orgId, chargerModel, ORG_ID, UNIT_ID]);

  // Fetch the additional charts only when "Show more" is clicked
  useEffect(() => {
    if (!showMoreCharts) {
      return; // Don't fetch if "Show more" hasn't been clicked
    }

    const dependencyKey = `${unitId}-${orgId}-${dateRange.start.getTime()}-${dateRange.end.getTime()}-${showMoreCharts}`;
    
    // Prevent duplicate calls in StrictMode
    if (additionalChartsFetchedRef.current === dependencyKey) return;
    additionalChartsFetchedRef.current = dependencyKey;

    const fetchCCSVoltageData = async () => {
      setCcsVoltageLoading(true);
      setCcsVoltageError(null);

      try {
        const fromDate = formatDateForAPIFrom(dateRange.start);
        const toDate = formatDateForAPITo(dateRange.end);
        
        const response = await chargerService.getCCSVoltage(ORG_ID, UNIT_ID, fromDate, toDate);
        
        if (response.success && response.data) {
          const merged = mergeCCSDataByTimestamp(response.data.data, 'ccs1_voltage', 'ccs2_voltage');
          setCcsVoltageData(merged);
        } else {
          setCcsVoltageError(response.message || 'Failed to fetch CCS voltage data');
        }
      } catch (error) {
        console.error('Error fetching CCS voltage data:', error);
        setCcsVoltageError(error instanceof Error ? error.message : 'Unknown error occurred');
      } finally {
        setCcsVoltageLoading(false);
      }
    };

    const fetchCCSCurrentData = async () => {
      setCcsCurrentLoading(true);
      setCcsCurrentError(null);

      try {
        const fromDate = formatDateForAPIFrom(dateRange.start);
        const toDate = formatDateForAPITo(dateRange.end);
        
        const response = await chargerService.getCCSCurrent(ORG_ID, UNIT_ID, fromDate, toDate);
        
        if (response.success && response.data) {
          const merged = mergeCCSDataByTimestamp(response.data.data, 'ccs1_current', 'ccs2_current');
          setCcsCurrentData(merged);
        } else {
          setCcsCurrentError(response.message || 'Failed to fetch CCS Current data');
        }
      } catch (error) {
        console.error('Error fetching CCS Current data:', error);
        setCcsCurrentError(error instanceof Error ? error.message : 'Unknown error occurred');
      } finally {
        setCcsCurrentLoading(false);
      }
    };

    const fetchBatteryTempData = async () => {
      // Skip fetching if model is Mjølner or Boostpoint
      if (chargerModel === 'Mjølner' || chargerModel === 'Boostpoint') {
        setBatteryTempData({ values: [], timestamps: [] });
        setBatteryTempLoading(false);
        return;
      }

      setBatteryTempLoading(true);
      setBatteryTempError(null);

      try {
        const fromDate = formatDateForAPIFrom(dateRange.start);
        const toDate = formatDateForAPITo(dateRange.end);
        
        const response = await chargerService.getBatteryTemperature(ORG_ID, UNIT_ID, fromDate, toDate);
        
        if (response.success && response.data) {
          const values = response.data.data.map((point: any) => point.bms_temp ?? null);
          const timestamps = response.data.data.map((point: any) => new Date(point.timestamp));
          
          setBatteryTempData({ values, timestamps });
        } else {
          setBatteryTempError(response.message || 'Failed to fetch Battery Temperature data');
        }
      } catch (error) {
        console.error('Error fetching Battery Temperature data:', error);
        setBatteryTempError(error instanceof Error ? error.message : 'Unknown error occurred');
      } finally {
        setBatteryTempLoading(false);
      }
    };

    const fetchBatteryVoltageData = async () => {
      // Skip fetching if model is Mjølner or Boostpoint
      if (chargerModel === 'Mjølner' || chargerModel === 'Boostpoint') {
        setBatteryVoltageData({ values: [], timestamps: [] });
        setBatteryVoltageLoading(false);
        return;
      }

      setBatteryVoltageLoading(true);
      setBatteryVoltageError(null);

      try {
        const fromDate = formatDateForAPIFrom(dateRange.start);
        const toDate = formatDateForAPITo(dateRange.end);
        
        const response = await chargerService.getBatteryVoltage(ORG_ID, UNIT_ID, fromDate, toDate);
        
        if (response.success && response.data) {
          const values = response.data.data.map((point: any) => point.battery_voltage ?? null);
          const timestamps = response.data.data.map((point: any) => new Date(point.timestamp));
          
          setBatteryVoltageData({ values, timestamps });
        } else {
          setBatteryVoltageError(response.message || 'Failed to fetch Battery Voltage data');
        }
      } catch (error) {
        console.error('Error fetching Battery Voltage data:', error);
        setBatteryVoltageError(error instanceof Error ? error.message : 'Unknown error occurred');
      } finally {
        setBatteryVoltageLoading(false);
      }
    };

    // Only fetch these 4 charts when "Show more" is clicked
    fetchCCSVoltageData();
    fetchCCSCurrentData();
    fetchBatteryTempData();
    fetchBatteryVoltageData();
  }, [showMoreCharts, dateRange, unitId, orgId, chargerModel, ORG_ID, UNIT_ID]);

  // Chart data - apply 15 min sampling when range > 3 days
  const chartData = useMemo(() => {
    // Start with originals
    let ccsVoltageT = ccsVoltageData.timestamps;
    let ccsVoltage1 = ccsVoltageData.ccs1Data;
    let ccsVoltage2 = ccsVoltageData.ccs2Data;

    let ccsPowerT = ccsPowerData.timestamps;
    let ccsPower1 = ccsPowerData.ccs1Data;
    let ccsPower2 = ccsPowerData.ccs2Data;

    let ccsCurrentT = ccsCurrentData.timestamps;
    let ccsCurrent1 = ccsCurrentData.ccs1Data;
    let ccsCurrent2 = ccsCurrentData.ccs2Data;

    let ccsSocT = ccsEVSOCData.timestamps;
    let ccsSoc1 = ccsEVSOCData.ccs1Data;
    let ccsSoc2 = ccsEVSOCData.ccs2Data;

    let battSocT = batterySOCData.timestamps;
    let battSocV = batterySOCData.values;

    let inletPowerT = inletPowerData.timestamps;
    let inletPowerV = inletPowerData.values;

    let battTempT = batteryTempData.timestamps;
    let battTempV = batteryTempData.values;

    let battVoltT = batteryVoltageData.timestamps;
    let battVoltV = batteryVoltageData.values;

    if (shouldDownsampleTo15Min) {
      // Downsample paired CCS series together to keep alignment
      if (ccsVoltageT.length) {
        const d = downsampleTo15Min(ccsVoltageT, ccsVoltage1, ccsVoltage2);
        ccsVoltageT = d.timestamps;
        [ccsVoltage1, ccsVoltage2] = d.series;
      }
      if (ccsPowerT.length) {
        const d = downsampleTo15Min(ccsPowerT, ccsPower1, ccsPower2);
        ccsPowerT = d.timestamps;
        [ccsPower1, ccsPower2] = d.series;
      }
      if (ccsCurrentT.length) {
        const d = downsampleTo15Min(ccsCurrentT, ccsCurrent1, ccsCurrent2);
        ccsCurrentT = d.timestamps;
        [ccsCurrent1, ccsCurrent2] = d.series;
      }
      if (ccsSocT.length) {
        const d = downsampleTo15Min(ccsSocT, ccsSoc1, ccsSoc2);
        ccsSocT = d.timestamps;
        [ccsSoc1, ccsSoc2] = d.series;
      }
      if (battSocT.length) {
        const d = downsampleTo15Min(battSocT, battSocV);
        battSocT = d.timestamps;
        [battSocV] = d.series;
      }
      if (inletPowerT.length) {
        const d = downsampleTo15Min(inletPowerT, inletPowerV);
        inletPowerT = d.timestamps;
        [inletPowerV] = d.series;
      }
      if (battTempT.length) {
        const d = downsampleTo15Min(battTempT, battTempV);
        battTempT = d.timestamps;
        [battTempV] = d.series;
      }
      if (battVoltT.length) {
        const d = downsampleTo15Min(battVoltT, battVoltV);
        battVoltT = d.timestamps;
        [battVoltV] = d.series;
      }
    }

    // Apply conversion for KE chargers: divide CCS Power and Inlet Power by 1000 (W to kW)
    const convertPowerToKW = (value: number | null): number | null => {
      if (value === null) return value;
      return value / 1000;
    };

    const finalCcsPower1 = shouldConvertToKW ? ccsPower1.map(convertPowerToKW) : ccsPower1;
    const finalCcsPower2 = shouldConvertToKW ? ccsPower2.map(convertPowerToKW) : ccsPower2;
    const finalInletPower = shouldConvertToKW ? inletPowerV.map(convertPowerToKW) : inletPowerV;

    return {
      ccsSpenning: ccsVoltage1,
      ccsSpenningCcs2: ccsVoltage2,
      ccsSpenningTimestamps: ccsVoltageT,
      ccsEffekt: finalCcsPower1,
      ccsEffektCcs2: finalCcsPower2,
      ccsEffektTimestamps: ccsPowerT,
      ccsStrom: ccsCurrent1,
      ccsStromCcs2: ccsCurrent2,
      ccsStromTimestamps: ccsCurrentT,
      kjoretoySoC: ccsSoc1,
      kjoretoySoCCcs2: ccsSoc2,
      kjoretoySoCTimestamps: ccsSocT,
      batterySOC: battSocV,
      batterySOCTimestamps: battSocT,
      inletPower: finalInletPower,
      inletPowerTimestamps: inletPowerT,
      batteryTemp: battTempV,
      batteryTempTimestamps: battTempT,
      batteryVoltage: battVoltV,
      batteryVoltageTimestamps: battVoltT,
    };
  }, [shouldDownsampleTo15Min, shouldConvertToKW, ccsVoltageData, ccsPowerData, ccsCurrentData, ccsEVSOCData, batterySOCData, inletPowerData, batteryTempData, batteryVoltageData]);


  // Format date for input value
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Handle zoom from chart selection (doesn't update original date range)
  const handleZoom = (start: Date, end: Date) => {
    setDateRange({ start, end });
    // Don't update originalDateRange - zoom should be resettable
  };

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Dashboard
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', mb: 3, gap: 2 }}>
        <Box sx={{ position: 'relative' }}>
          <input
            type="date"
            value={formatDateForInput(dateRange.start)}
            onChange={(e) => {
              if (e.target.value) {
                const startDate = new Date(e.target.value);
                startDate.setHours(0, 0, 0, 0); // Set to start of day (00:00:00)
                setDateRange(prev => ({ ...prev, start: startDate }));
                // Update original date range when user manually changes date picker
                setOriginalDateRange(prev => ({ ...prev, start: startDate }));
              }
            }}
            style={{
              width: '150px',
              height: '40px',
              padding: '8px 12px',
              fontSize: '16px',
              fontFamily: 'Montserrat',
              border: '1px solid rgba(0,0,0,0.23)',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: 'rgba(0,0,0,0.87)',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#1976d2';
              e.target.style.borderWidth = '2px';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(0,0,0,0.23)';
              e.target.style.borderWidth = '1px';
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: '-8px',
              left: '12px',
              backgroundColor: '#FAFAFA',
              padding: '0 4px',
              fontSize: '12px',
              color: 'rgba(0,0,0,0.6)',
              fontFamily: 'Montserrat',
            }}
          >
            Start Date
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontFamily: 'Montserrat' }}>
          –
        </Typography>
        <Box sx={{ position: 'relative' }}>
          <input
            type="date"
            value={formatDateForInput(dateRange.end)}
            onChange={(e) => {
              if (e.target.value) {
                const endDate = new Date(e.target.value);
                endDate.setHours(23, 59, 59, 999); // Set to end of day (23:59:59)
                setDateRange(prev => ({ ...prev, end: endDate }));
                // Update original date range when user manually changes date picker
                setOriginalDateRange(prev => ({ ...prev, end: endDate }));
              }
            }}
            style={{
              width: '150px',
              height: '40px',
              padding: '8px 12px',
              fontSize: '16px',
              fontFamily: 'Montserrat',
              border: '1px solid rgba(0,0,0,0.23)',
              borderRadius: '4px',
              backgroundColor: 'transparent',
              color: 'rgba(0,0,0,0.87)',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#1976d2';
              e.target.style.borderWidth = '2px';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(0,0,0,0.23)';
              e.target.style.borderWidth = '1px';
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              top: '-8px',
              left: '12px',
              backgroundColor: '#FAFAFA',
              padding: '0 4px',
              fontSize: '12px',
              color: 'rgba(0,0,0,0.6)',
              fontFamily: 'Montserrat',
            }}
          >
            End Date
          </Typography>
        </Box>
        {isZoomed && (
          <Button
            variant="outlined"
            onClick={handleResetZoom}
            sx={{
              borderColor: 'rgba(51,122,92,0.5)',
              color: '#337a5c',
              fontSize: '14px',
              fontFamily: 'Montserrat',
              fontWeight: 500,
              letterSpacing: '0.46px',
              textTransform: 'none',
              px: 2,
              py: 1,
              height: '40px',
            }}
          >
            Reset zoom
          </Button>
        )}
{/*         <Button
          variant="outlined"
          startIcon={<TrendingUp sx={{ fontSize: 24 }} />}
          sx={{
            borderColor: 'rgba(51,122,92,0.5)',
            color: '#337a5c',
            fontSize: '16px',
            fontFamily: 'Montserrat',
            fontWeight: 500,
            letterSpacing: '0.46px',
            textTransform: 'uppercase',
            px: 3,
            py: 1,
          }}
        >
          Live
        </Button> */}
      </Box>

      {/* Charts section */}
      {chargerNameLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          
          {/* Charts grid */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <MeasurementChart
                title="CCS Power [kW]"
                data={chartData.ccsEffekt}
                ccs2Data={chartData.ccsEffektCcs2}
                timestamps={chartData.ccsEffektTimestamps}
                unit="kW"
                color="#2C3D5A"
                dateRange={dateRange}
                loading={ccsPowerLoading}
                error={ccsPowerError || undefined}
                onZoom={handleZoom}
              />
            </Grid>
          <Grid item xs={12} md={6}>
            <MeasurementChart
              title="Vehicle Battery Level [%]"
              data={chartData.kjoretoySoC}
              ccs2Data={chartData.kjoretoySoCCcs2}
              timestamps={chartData.kjoretoySoCTimestamps}
              unit="%"
              color="#2C3D5A"
              dateRange={dateRange}
              loading={ccsEVSOCLoading}
              error={ccsEVSOCError || undefined}
              onZoom={handleZoom}
            />
          </Grid>
          {!shouldHideBatteryCharts && (
            <Grid item xs={12} md={6}>
              <MeasurementChart
                title="Battery Level [%]"
                data={chartData.batterySOC}
                timestamps={chartData.batterySOCTimestamps}
                unit="%"
                color="#2C3D5A"
                dateRange={dateRange}
                loading={batterySOCLoading}
                error={batterySOCError || undefined}
                onZoom={handleZoom}
              />
            </Grid>
          )}
          <Grid item xs={12} md={shouldHideBatteryCharts ? 12 : 6}>
            <MeasurementChart
              title="Inlet Power [kW]"
              data={chartData.inletPower}
              timestamps={chartData.inletPowerTimestamps}
              unit="kW"
              color="#2C3D5A"
              dateRange={dateRange}
              loading={inletPowerLoading}
              error={inletPowerError || undefined}
              onZoom={handleZoom}
            />
          </Grid>
        </Grid>

        {/* Additional charts - shown when "Show more" is clicked */}
        {showMoreCharts && (
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <MeasurementChart
                  title="CCS Voltage [V]"
                  data={chartData.ccsSpenning}
                  ccs2Data={chartData.ccsSpenningCcs2}
                  timestamps={chartData.ccsSpenningTimestamps}
                  unit="V"
                  color="#2C3D5A"
                  dateRange={dateRange}
                  loading={ccsVoltageLoading}
                  error={ccsVoltageError || undefined}
                  onZoom={handleZoom}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MeasurementChart
                  title="CCS Current [A]"
                  data={chartData.ccsStrom}
                  ccs2Data={chartData.ccsStromCcs2}
                  timestamps={chartData.ccsStromTimestamps}
                  unit="A"
                  color="#2C3D5A"
                  dateRange={dateRange}
                  loading={ccsCurrentLoading}
                  error={ccsCurrentError || undefined}
                  onZoom={handleZoom}
                />
              </Grid>
              {!shouldHideBatteryCharts && (
                <Grid item xs={12} md={6}>
                  <MeasurementChart
                    title="Battery Temperature [°C]"
                    data={chartData.batteryTemp}
                    timestamps={chartData.batteryTempTimestamps}
                    unit="°C"
                    color="#2C3D5A"
                    dateRange={dateRange}
                    loading={batteryTempLoading}
                    error={batteryTempError || undefined}
                    onZoom={handleZoom}
                  />
                </Grid>
              )}
              {!shouldHideBatteryCharts && (
                <Grid item xs={12} md={6}>
                  <MeasurementChart
                    title="Battery Voltage [V]"
                    data={chartData.batteryVoltage}
                    timestamps={chartData.batteryVoltageTimestamps}
                    unit="V"
                    color="#2C3D5A"
                    dateRange={dateRange}
                    loading={batteryVoltageLoading}
                    error={batteryVoltageError || undefined}
                    onZoom={handleZoom}
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}

        {/* Show more button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setShowMoreCharts(!showMoreCharts)}
            sx={{
              borderColor: 'rgba(51,122,92,0.5)',
              color: '#337a5c',
              fontSize: '16px',
              fontFamily: 'Montserrat',
              fontWeight: 500,
              letterSpacing: '0.46px',
              textTransform: 'uppercase',
              px: 6,
              py: 1,
            }}
          >
            {showMoreCharts ? 'show less' : 'show more'}
          </Button>
        </Box>
      </Box>
      )}
    </Box>
  );
};

export default ChargerDashboard;
